import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ShoppingCart, Plus, Package, Users, LayoutGrid, Trash2, X, Search, Camera, Receipt, Save,
  Minus, Image as ImageIcon, BarChart3, Settings, Printer, Clock, ArrowUpRight, Edit3,
  FileText, Download, AlertTriangle, Ban, UserCheck, Share2, Copy, AlertOctagon, RefreshCw, Truck,
  RotateCcw, ChevronLeft, ChevronRight, ScanBarcode, UserCircle2, Layers, Lock, LogIn, Menu, LogOut,
  HelpCircle, CheckSquare, Square, MapPin, Phone, PauseCircle, PlayCircle, AlertCircle, Banknote,
  CreditCard, Smartphone, BookOpen, Crown, Unlock, Globe, Wallet, History, FileCheck, Boxes, PenLine,
  CreditCard as CardIcon, Store, ArrowRightLeft, UserCog, Briefcase, FileOutput, Megaphone, UserPlus,
  Link, MonitorSmartphone, Contact, FileMinus, MessageSquare } from 'lucide-react';
import { apiCall, generateId, formatPrice, formatDate, compressImage,
  base64ToFile, printHtmlDiv, printRawHtml, printBarcodeDiv } from '../utils/utils.js';

const CustomerHistoryModal = ({ customer, onClose, onReceivePayment, onShareLedger }) => {
    const [filterFrom, setFilterFrom] = useState('');
    const [filterTo, setFilterTo] = useState('');
    const [ledger, setLedger] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!customer?.id) return;
        setLoading(true);
        apiCall('getCustomerLedger', { customer_id: customer.id })
            .then(d => { setLedger(d); setLoading(false); })
            // A rejected request (network failure, server unreachable) previously left
            // `ledger` as null, which rendered as "No transactions found" — falsely
            // implying the customer genuinely has no history instead of showing that
            // the request failed. Now it surfaces as the same "Server error" state the
            // {error} response path already handles.
            .catch(() => { setLedger({ error: 'Could not reach the server. Please check your connection and try again.' }); setLoading(false); });
    }, [customer?.id]);

    if (!customer) return null;

    // Ledger entry type → display label/status. 'sale' rows show the actual payment method
    // (Cash/Card/Credit/etc.) so it's clear which sales are debt and which were already paid.
    let ledgerEntries = (ledger?.entries || []).map(e => ({
        ...e,
        status: e.type === 'sale' ? (e.payment_method || 'Sale') : e.type === 'return' ? 'Returned' : e.type === 'qist' ? 'Qist Due' : e.type === 'opening' ? 'Opening Bal' : 'Payment',
    }));

    if (filterFrom) ledgerEntries = ledgerEntries.filter(e => new Date(e.date.replace(' ','T')) >= new Date(`${filterFrom}T00:00:00`));
    if (filterTo)   ledgerEntries = ledgerEntries.filter(e => new Date(e.date.replace(' ','T')) <= new Date(`${filterTo}T23:59:59`));
    ledgerEntries.sort((a,b) => new Date(a.date.replace(' ','T')) - new Date(b.date.replace(' ','T')));

    // Running balance (recomputed after any local date filtering so the filtered view still ties out)
    let running = 0;
    const rows = ledgerEntries.map(e => {
        running += e.debit - e.credit;
        return { ...e, balance: running };
    });

    const totalDebit  = rows.reduce((a,r) => a + r.debit, 0);
    const totalCredit = rows.reduce((a,r) => a + r.credit, 0);
    const outstanding = totalDebit - totalCredit;

    return (
        <div className="fixed inset-0 bg-[#1a1a1a]/60 backdrop-blur-sm z-[60] flex items-center justify-center p-2 sm:p-4 animate-in fade-in duration-200">
            <div className="bg-white w-full max-w-2xl rounded-xl shadow-lg border border-[#e1e3e5] overflow-hidden flex flex-col max-h-[95vh]">
                {/* Header */}
                <div className="p-4 border-b border-[#e1e3e5] flex justify-between items-center bg-[#f6f7f8]">
                    <div>
                        <h3 className="font-bold text-[#1a1a1a] flex items-center gap-2"><BookOpen size={18} className="text-[#008060]"/> {customer?.name} — Account Ledger</h3>
                        <p className="text-xs text-[#5c5f62] mt-0.5">{customer?.phone} · {customer?.address}</p>
                    </div>
                    <button onClick={onClose} className="text-[#5c5f62]"><X size={20}/></button>
                </div>

                {/* Summary cards */}
                <div className="grid grid-cols-3 divide-x divide-[#e1e3e5] border-b border-[#e1e3e5] bg-white">
                    <div className="p-3 text-center">
                        <p className="text-[10px] font-bold text-[#5c5f62] uppercase">Total Billed</p>
                        <p className="text-base font-black text-[#1a1a1a]">{formatPrice(totalDebit)}</p>
                    </div>
                    <div className="p-3 text-center">
                        <p className="text-[10px] font-bold text-[#5c5f62] uppercase">Total Paid</p>
                        <p className="text-base font-black text-[#008060]">{formatPrice(totalCredit)}</p>
                    </div>
                    <div className="p-3 text-center">
                        <p className="text-[10px] font-bold text-[#5c5f62] uppercase">Balance Due</p>
                        <p className={`text-base font-black ${outstanding > 0 ? 'text-[#d82c0d]' : 'text-[#008060]'}`}>{formatPrice(Math.abs(outstanding))}</p>
                        {outstanding > 0 && <p className="text-[8px] font-bold text-[#d82c0d] uppercase">Owes</p>}
                        {outstanding <= 0 && <p className="text-[8px] font-bold text-[#008060] uppercase">Clear</p>}
                    </div>
                    {customer?.credit_limit > 0 && (
                        <div className="p-3 text-center border-l border-[#e1e3e5]">
                            <p className="text-[10px] font-bold text-[#5c5f62] uppercase">Credit Limit</p>
                            <p className="text-base font-black text-[#7c3aed]">{formatPrice(customer.credit_limit)}</p>
                            <p className={`text-[8px] font-bold uppercase ${outstanding >= customer.credit_limit ? 'text-[#d82c0d]' : 'text-[#5c5f62]'}`}>
                                {outstanding >= customer.credit_limit ? 'LIMIT HIT' : `${formatPrice(Math.max(0, customer.credit_limit - outstanding))} left`}
                            </p>
                        </div>
                    )}
                </div>

                {/* Filters + actions */}
                <div className="p-3 bg-[#f6f7f8] border-b border-[#e1e3e5] flex flex-wrap gap-2 items-center">
                    <input type="date" value={filterFrom} onChange={e=>setFilterFrom(e.target.value)} className="p-1.5 border border-[#c9cccf] rounded-lg text-xs outline-none"/>
                    <span className="text-[#5c5f62] text-xs">→</span>
                    <input type="date" value={filterTo} onChange={e=>setFilterTo(e.target.value)} className="p-1.5 border border-[#c9cccf] rounded-lg text-xs outline-none"/>
                    <div className="flex-1"/>
                    {outstanding > 0 && onReceivePayment && (
                        <button onClick={() => { onClose(); onReceivePayment(customer.id, customer.name); }} className="text-xs bg-[#008060] text-white px-3 py-1.5 rounded-lg font-semibold flex items-center gap-1 hover:bg-[#006e52]"><CreditCard size={12}/> Record Payment</button>
                    )}
                    <button onClick={() => {
                        const lines = rows.map(r =>
                            `${formatDate(r.date).padEnd(12)} ${r.debit>0?`Dr Rs${r.debit.toFixed(2)}`:'            '} ${r.credit>0?`Cr Rs${r.credit.toFixed(2)}`:'             '} Bal Rs${Math.abs(r.balance).toFixed(2)}${r.balance>0?' Dr':' Cr'}`
                        ).join('\n');
                        const waText = `*${customer.name} — Account Statement*\n${customer.phone||''}\n` +
                            `Period: ${filterFrom||'All'} → ${filterTo||'Today'}\n\n` +
                            `Total Billed: Rs ${totalDebit.toFixed(2)}\nTotal Paid: Rs ${totalCredit.toFixed(2)}\nBalance Due: Rs ${Math.abs(outstanding).toFixed(2)} ${outstanding>0?'Dr':'Cr'}\n\n` +
                            lines;
                        const phone = (customer.phone||'').replace(/\D/g,'');
                        window.open(`https://wa.me/${phone}?text=${encodeURIComponent(waText)}`, '_blank');
                    }} className="text-xs bg-[#25D366] text-white px-3 py-1.5 rounded-lg font-semibold flex items-center gap-1"><Phone size={12}/> WhatsApp</button>
                    <button onClick={() => {
                        const rowsHtml = rows.map(r => `
                            <tr>
                                <td>${formatDate(r.date)}</td>
                                <td>${r.desc}</td>
                                <td class="r red">${r.debit>0?'Rs '+r.debit.toFixed(2):'—'}</td>
                                <td class="r grn">${r.credit>0?'Rs '+r.credit.toFixed(2):'—'}</td>
                                <td class="r ${r.balance>0?'red':'grn'}">${'Rs '+Math.abs(r.balance).toFixed(2)+' '+(r.balance>0?'Dr':'Cr')}</td>
                            </tr>`).join('');
                        const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
                        <title>Statement</title>
                        <style>
                            @page{size:80mm auto;margin:4mm}
                            body{font-family:'Courier New',monospace;font-size:10px;color:#000;width:72mm}
                            .c{text-align:center}.b{font-weight:bold}.r{text-align:right}
                            .red{color:#d82c0d}.grn{color:#008060}
                            table{width:100%;border-collapse:collapse;font-size:9px}
                            th,td{padding:2px 1px;border-bottom:1px solid #eee;vertical-align:top}
                            th{font-weight:bold;border-bottom:2px solid #000}
                            tfoot td{font-weight:bold;border-top:2px solid #000}
                            .hr{border-top:1px dashed #000;margin:4px 0}
                        </style></head><body>
                        <div class="c b" style="font-size:13px">${customer.name}</div>
                        <div class="c" style="font-size:9px">${customer.phone||''} ${customer.address?'· '+customer.address:''}</div>
                        <div class="hr"></div>
                        <div class="c b">ACCOUNT STATEMENT</div>
                        <div class="c" style="font-size:9px">${filterFrom||'All'} → ${filterTo||new Date().toISOString().slice(0,10)}</div>
                        <div class="hr"></div>
                        <table>
                            <thead><tr><th>Date</th><th>Desc</th><th class="r red">Dr</th><th class="r grn">Cr</th><th class="r">Balance</th></tr></thead>
                            <tbody>${rowsHtml}</tbody>
                            <tfoot><tr>
                                <td colspan="2" class="b">Totals</td>
                                <td class="r red b">Rs ${totalDebit.toFixed(2)}</td>
                                <td class="r grn b">Rs ${totalCredit.toFixed(2)}</td>
                                <td class="r ${outstanding>0?'red':'grn'} b">Rs ${Math.abs(outstanding).toFixed(2)} ${outstanding>0?'Dr':'Cr'}</td>
                            </tr></tfoot>
                        </table>
                        <div class="hr"></div>
                        <div class="c" style="font-size:9px">Printed: ${new Date().toLocaleString('en-PK')}</div>
                        </body></html>`;
                        printRawHtml(html, { lang: localStorage.getItem('pos_lang') || 'en' });
                    }} className="text-xs bg-[#1a1a1a] text-white px-3 py-1.5 rounded-lg font-semibold flex items-center gap-1"><Printer size={12}/> Print Statement</button>
                    {customer?.khata_balance > 0 && (
                        <button onClick={async () => {
                            const res = await apiCall('sendKhataReminder', { customer_id: customer.id });
                            if (res?.error) alert(res.error + (res.message_preview ? '\n\nPreview:\n' + res.message_preview : ''));
                            else if (res?.success === false) alert('SMS not configured.\n\nPreview:\n' + (res.message_preview || ''));
                            else alert('SMS reminder sent!');
                        }} className="text-xs bg-[#7c3aed] text-white px-3 py-1.5 rounded-lg font-semibold flex items-center gap-1"><MessageSquare size={12}/> SMS Reminder</button>
                    )}
                </div>

                {/* Ledger table */}
                <div className="overflow-y-auto flex-1">
                    {loading ? (
                        <div className="text-center py-12 text-[#5c5f62]"><p className="text-sm">Loading ledger…</p></div>
                    ) : ledger?.error ? (
                        <div className="text-center py-12 px-6 text-[#d82c0d]"><p className="text-sm font-semibold">Server error: {ledger.error}</p></div>
                    ) : rows.length === 0 ? (
                        <div className="text-center py-12 text-[#5c5f62]"><BookOpen size={40} className="mx-auto mb-4 opacity-40"/><p className="text-sm">No transactions found for this period.</p></div>
                    ) : (
                        <table className="w-full text-xs">
                            <thead className="sticky top-0 bg-[#f6f7f8] border-b border-[#e1e3e5]">
                                <tr>
                                    <th className="p-3 text-left font-bold text-[#5c5f62] uppercase">Date</th>
                                    <th className="p-3 text-left font-bold text-[#5c5f62] uppercase">Description</th>
                                    <th className="p-3 text-right font-bold text-[#5c5f62] uppercase">Amount</th>
                                    <th className="p-3 text-right font-bold text-[#d82c0d] uppercase">Debit (Owe)</th>
                                    <th className="p-3 text-right font-bold text-[#008060] uppercase">Credit (Paid)</th>
                                    <th className="p-3 text-right font-bold text-[#1a1a1a] uppercase">Balance</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-[#f6f7f8]">
                                {rows.map((r, i) => {
                                    const isCreditMove = r.debit > 0 || r.credit > 0;
                                    return (
                                    <tr key={i} className={`hover:bg-[#f6f7f8] ${r.type === 'payment' ? 'bg-[#f0fdf4]' : r.type === 'return' && isCreditMove ? 'bg-[#fef2f2]' : !isCreditMove ? 'opacity-70' : ''}`}>
                                        <td className="p-3 text-[#5c5f62] whitespace-nowrap">{formatDate(r.date)}</td>
                                        <td className="p-3 text-[#1a1a1a] max-w-[180px]">
                                            <p className="truncate font-medium">{r.desc}</p>
                                            <p className="text-[10px] text-[#5c5f62]">
                                                {r.id ? `#${r.id.slice(0,8).toUpperCase()} · ` : ''}<span className={`font-bold ${r.type === 'payment' || (r.type === 'return' && isCreditMove) ? 'text-[#008060]' : 'text-[#5c5f62]'}`}>{r.status}</span>
                                                {r.type === 'sale' && !isCreditMove && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-[#e1e3e5] text-[#5c5f62] font-bold">Paid in full</span>}
                                            </p>
                                        </td>
                                        <td className="p-3 text-right text-[#5c5f62]">{formatPrice(r.amount ?? (r.debit || r.credit))}</td>
                                        <td className="p-3 text-right font-bold text-[#d82c0d]">{r.debit > 0 ? formatPrice(r.debit) : '—'}</td>
                                        <td className="p-3 text-right font-bold text-[#008060]">{r.credit > 0 ? formatPrice(r.credit) : '—'}</td>
                                        <td className={`p-3 text-right font-black ${r.balance > 0 ? 'text-[#d82c0d]' : 'text-[#008060]'}`}>{formatPrice(Math.abs(r.balance))}{r.balance > 0 ? ' Dr' : ' Cr'}</td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                            <tfoot className="sticky bottom-0 bg-[#1a1a1a] text-white border-t-2 border-[#1a1a1a]">
                                <tr>
                                    <td className="p-3 font-black text-xs uppercase" colSpan={3}>Totals</td>
                                    <td className="p-3 text-right font-black text-[#fca5a5]">{formatPrice(totalDebit)}</td>
                                    <td className="p-3 text-right font-black text-[#86efac]">{formatPrice(totalCredit)}</td>
                                    <td className={`p-3 text-right font-black ${outstanding > 0 ? 'text-[#fca5a5]' : 'text-[#86efac]'}`}>{formatPrice(Math.abs(outstanding))} {outstanding > 0 ? 'Dr' : 'Cr'}</td>
                                </tr>
                            </tfoot>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CustomerHistoryModal;
